import { CameraControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useCallback, useMemo, type RefObject } from 'react';

import { useInputActionHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import {
    createCameraMouseButtons,
    useCameraControlsEvents
} from 'app/modules/wallforming/components/scenes/camera-controls/hooks/useCameraControlsEvents';
import { usePreviewCameraHandler } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/usePreviewCameraHandler';
import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';

interface PreviewCameraControlsProps {
    previewCameraControlsRef: RefObject<CameraControls | null>;
    onCenterCamera: () => void;
    onRest: () => void;
}

export const PreviewCameraControls = ({
    previewCameraControlsRef,
    onCenterCamera,
    onRest
}: PreviewCameraControlsProps) => {
    const raycaster = useThree((state) => state.raycaster);
    const gl = useThree((state) => state.gl);
    const previewSceneElement = gl.domElement.closest('[data-input-scene]');
    const mouseButtons = useMemo(() => createCameraMouseButtons(), []);

    const handlePreviewCameraControlsRef = useCallback(
        (cameraControls: CameraControls | null) => {
            previewCameraControlsRef.current = cameraControls;
            if (cameraControls) {
                cameraControls.camera.layers.enableAll();

                if (raycaster) {
                    raycaster.layers.disableAll();
                    raycaster.layers.enable(LayerType.RAYCAST);
                }
            }
        },
        [previewCameraControlsRef, raycaster]
    );

    useCameraControlsEvents({ cameraControlsRef: previewCameraControlsRef, isMainCamera: false });
    usePreviewCameraHandler({ cameraControlsRef: previewCameraControlsRef });
    useInputActionHandler(InputActionId.CameraCenter, (event) => {
        if (event.target instanceof Node && previewSceneElement?.contains(event.target)) {
            onCenterCamera();
        }
    });

    return (
        <CameraControls
            ref={handlePreviewCameraControlsRef}
            dollySpeed={1.5}
            draggingSmoothTime={0.07}
            infinityDolly={true}
            mouseButtons={mouseButtons}
            truckSpeed={2}
            dollyToCursor
            makeDefault
            onRest={onRest}
        />
    );
};
